// src/components/DailySummaryView.jsx
import React from 'react';
import { Box, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Grid } from '@mui/material';

const formatDescriptionQuantityMultiple = (description) => {
  if (typeof description !== 'string') {
    return description;
  }
  return description.replace(/(\d+(?:\.\d+)?)\s*([A-Za-zÀ-ÖØ-öø-ÿ]+)/g, (match, numStr, unitStr) => {
    const numberValue = parseFloat(numStr);
    if (numberValue % 1 === 0) {
      return `${parseInt(numberValue, 10)} ${unitStr}`;
    } else {
      return `${String(numberValue)} ${unitStr}`;
    }
  });
};

const formatCurrency = (value) => {
  const number = Number(value);
  if (isNaN(number)) {
    return typeof value === 'string' && value.trim() === '' ? '' : String(value);
  }
  return number.toFixed(2);
};

// ✅ Função para converter método de pagamento para formato completo
const getPaymentMethodDisplay = (abbr) => {
  const paymentMethods = {
    'CA': 'Cash',
    'RC': 'Room Charge', 
    'CC': 'Credit Card',
    'SQ': 'Square'
  };
  return paymentMethods[abbr] || abbr;
};

const DailySummaryView = React.forwardRef(({ summaryData }, ref) => {
  if (!summaryData) {
    return <Typography>Loading summary...</Typography>;
  }

  const {
    date,
    name,
    resort,
    sales_items = [],
    total_cash,
    total_rc,
    total_cc,
    total_sq,
    grand_total,
    total_tips,
    // Campos de material
    material_val = '',
    pre_mades_in_val = 0,
    pre_mades_out_val = 0,
    combs_in_val = 0,
    combs_out_val = 0,
    metal_clips_val = 0,
    plastic_clips_val = 0,
    scissors_val = 0,
  } = summaryData;

  const minRowsInTable = 28;
  const emptyRowsCount = Math.max(0, minRowsInTable - sales_items.length);
  const emptyRows = Array(emptyRowsCount).fill(null).map((_, index) => ({
    key: `empty-${index}`,
    number: '',
    description: '',
    subtotal: '',
    tip: '',
    payment_method_abbr: '',
  }));

  const displayItems = [
    ...sales_items.map((item, index) => ({ ...item, key: `sale-${index}` })),
    ...emptyRows
  ];

  // ✅ NOVO: Adicionar linha de totais
  const totalsRow = {
    key: 'totals-row',
    number: '',
    description: 'TOTALS:',
    subtotal: formatCurrency(grand_total),
    tip: formatCurrency(total_tips),
    payment_method_abbr: '',
  };

  const allDisplayItems = [...displayItems, totalsRow];

  const baseCellStyle = {
    border: '1px solid black',
    padding: '1px 3px',
    fontSize: '8pt',
    fontFamily: '"Arial Narrow", Arial, sans-serif',
    textAlign: 'center',
    verticalAlign: 'middle',
    height: '18px',
    boxSizing: 'border-box',
  };

  const headerCellStyle = {
    ...baseCellStyle,
    fontWeight: 'bold',
    backgroundColor: '#D3D3D3',
    color: 'black',
  };

  // ✅ NOVO: Estilo para linha de totais
  const totalsCellStyle = {
    ...baseCellStyle,
    fontWeight: 'bold',
    backgroundColor: '#E6E6E6',
    color: 'black',
  };

  const overallWidth = '780px';

  const bottomGridItemStyle = {
    fontSize: '8pt',
    padding: '2px 4px',
    fontFamily: '"Arial Narrow", Arial, sans-serif',
    color: '#222',
    lineHeight: '1.3'
  };

  return (
    <Box
      ref={ref}
      sx={{
        padding: '10px',
        backgroundColor: 'white',
        width: overallWidth,
        fontFamily: '"Arial Narrow", Arial, sans-serif',
        color: 'black',
        border: '1px solid black',
        boxSizing: 'border-box',
      }}
    >
      <Typography
        variant="h5"
        sx={{
          textAlign: 'center',
          fontWeight: 'bold',
          mb: 1,
          fontSize: '14pt',
          fontFamily: '"Arial Black", Gadget, sans-serif',
          textTransform: 'uppercase',
        }}
      >
        DAILY LOG SHEET
      </Typography>

      <Box sx={{ mb: 1 }}>
        <Typography sx={{ fontSize: '10pt', mb: 0.5 }}><b>DATE:</b> {date}</Typography>
        <Typography sx={{ fontSize: '10pt', mb: 0.5 }}><b>NAME:</b> {name}</Typography>
        <Typography sx={{ fontSize: '10pt', mb: 0.5 }}><b>RESORT:</b> {resort}</Typography>
      </Box>

      <TableContainer component={Paper} sx={{ boxShadow: 'none', border: 'none' }}>
        <Table size="small" aria-label="daily summary table" sx={{ tableLayout: 'fixed', borderCollapse: 'collapse', width: '100%' }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ ...headerCellStyle, width: '80px' }}>NUMBER</TableCell>
              <TableCell sx={{ ...headerCellStyle, width: '280px' }}>DESCRIPTION</TableCell>
              <TableCell sx={{ ...headerCellStyle, width: '90px' }}>SUB TOTAL</TableCell>
              <TableCell sx={{ ...headerCellStyle, width: '90px' }}>TIP</TableCell>
              <TableCell sx={{ ...headerCellStyle, width: '120px' }}>PAYMENT METHOD</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {allDisplayItems.map((item, index) => {
              const isTotalsRow = item.key === 'totals-row';
              const cellStyle = isTotalsRow ? totalsCellStyle : baseCellStyle;
              
              return (
                <TableRow key={item.key}>
                  <TableCell sx={cellStyle}>{item.number}</TableCell>
                  <TableCell sx={{ 
                    ...cellStyle, 
                    textAlign: isTotalsRow ? 'right' : 'left',
                    fontWeight: isTotalsRow ? 'bold' : 'normal'
                  }}>
                    {item.description ? 
                      (isTotalsRow ? item.description : formatDescriptionQuantityMultiple(item.description)) 
                      : ''}
                  </TableCell>
                  <TableCell sx={cellStyle}>
                    {item.subtotal !== undefined && item.subtotal !== '' ? 
                      (isTotalsRow ? item.subtotal : formatCurrency(item.subtotal)) : ''}
                  </TableCell>
                  <TableCell sx={cellStyle}>
                    {item.tip ? (isTotalsRow ? item.tip : formatCurrency(item.tip)) : ''}
                  </TableCell>
                  <TableCell sx={cellStyle}>
                    {item.payment_method_abbr ? getPaymentMethodDisplay(item.payment_method_abbr) : ''}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {/* ✅ ATUALIZADO: Resumo por método de pagamento sem grand total e tips */}
      <Box sx={{ mt: '16px' }}>
        <Grid container spacing={2} sx={{ fontSize: '9pt', fontFamily: '"Arial Narrow", Arial, sans-serif' }}>
          <Grid item xs={3}><Typography sx={{ fontWeight: 'bold' }}>TOTAL CASH: {formatCurrency(total_cash)}</Typography></Grid>
          <Grid item xs={3}><Typography sx={{ fontWeight: 'bold' }}>TOTAL RC: {formatCurrency(total_rc)}</Typography></Grid>
          <Grid item xs={3}><Typography sx={{ fontWeight: 'bold' }}>TOTAL CC: {formatCurrency(total_cc)}</Typography></Grid>
          <Grid item xs={3}><Typography sx={{ fontWeight: 'bold' }}>TOTAL SQ: {formatCurrency(total_sq)}</Typography></Grid>
        </Grid>
      </Box>

      {/* Seção de Materiais */}
      <Box sx={{ mt: '16px', borderTop: '1px solid #888', pt: '8px' }}>
        <Typography sx={{ fontSize: '10pt', fontWeight: 'bold', mb: 0.5 }}>MATERIALS & SUPPLIES:</Typography>
        {material_val && (
            <Typography sx={{...bottomGridItemStyle, fontStyle: 'italic', mb: 0.5}}>Description: {material_val}</Typography>
        )}
        <Grid container spacing={1}>
          <Grid item xs={3}><Typography sx={bottomGridItemStyle}>Pre-Mades In: {pre_mades_in_val}</Typography></Grid>
          <Grid item xs={3}><Typography sx={bottomGridItemStyle}>Pre-Mades Out: {pre_mades_out_val}</Typography></Grid>
          <Grid item xs={3}><Typography sx={bottomGridItemStyle}>Combs In: {combs_in_val}</Typography></Grid>
          <Grid item xs={3}><Typography sx={bottomGridItemStyle}>Combs Out: {combs_out_val}</Typography></Grid>
          <Grid item xs={3}><Typography sx={bottomGridItemStyle}>Metal Clips: {metal_clips_val}</Typography></Grid>
          <Grid item xs={3}><Typography sx={bottomGridItemStyle}>Plastic Clips: {plastic_clips_val}</Typography></Grid>
          <Grid item xs={3}><Typography sx={bottomGridItemStyle}>Scissors: {scissors_val}</Typography></Grid>
        </Grid>
      </Box>
    </Box>
  );
});

export default DailySummaryView;